<template lang="pug">
.date-input.box(
	:class='[{ open, optional : options.optional, "has-value" : localValue !== "" }, options.styleType || ""]'
	@mouseleave='setCloseTimeout'
	@mouseover='clearCloseTimeout'
	@keydown.esc='open = false'
	@keydown.passive='focusInput'
)
	.select-display(
		:class='{ "has-min-label" : options.minimalLabel && ( localValue !== "" || hasErrors ) }'
		:tabindex='lastTabIndex'
		@keydown.enter='toggleDropdown'
		@click='toggleDropdown'
	)
		p.minimal-label.highlight-text(v-if='options.minimalLabel') {{ options.minimalLabel }}
			span.red.text-error(v-if='hasErrors') - {{ displayError }}

		.input-container(v-if='options.typeDate')
			input.input-date(
				type='text'
				:placeholder='displayText'
				v-model='typedValue'
				@click='highlightAll'
				ref='textInput'
				@keydown='stopIfAlphaNumeric'
				:tab-index='lastTabIndex'
				@blur='checkForYear'
			)

		p.main-display(v-else) {{ displayText }}

	date-dropdown(
		@start-date-changed='setLocalValue( $event )'
		@range-changed='setLocalValueFromRange( $event )'
		:parent-val='passedValue'
		:range='options.allowRange || false'
		:clearable='true'
		:open='open'
		@clear='localValue = ""; passedValue = ""; open = false'
		@done='open = false'
	)
</template>

<script>
import DateDropdown from '@/components/DateDropdown';
import { dateOf, months } from '@/lib/time';

export default {
	name : 'date-input',

	props : {

		default : String,

		options : {
			type : [Object, Array],
		},

		field : {
			type : Object
		},

		errors : {
			type : [Object, String, Boolean],
		},

		value : {
			type : [String, Number, Object],
		},

		lastTabIndex : {
			type : Number,
		}

	},

	data : () => ( {
		localValue  : '', // what's emitted
		passedValue : '', // what's passed down to DateDropdown
		typedValue  : '', // what the user typed
		open        : false,

		closeTimeout : null,
		displayError : null,
	} ),

	computed : {

		displayText() {
			const { options : o, localValue } = this;

			const placeholder = ( () => {

				if ( o.defaultText ) {
					return o.defaultText;
				}

				if ( o.minimalLabel ) {
					return o.minimalLabel;
				}

				return 'Select a date';
			} )();

			const dateString = ( () => {
				if ( !localValue ) {
					return '';
				}

				if ( typeof localValue !== 'object' ) {
					return dateOf( localValue );
				}

				const { before, after } = localValue;
				return `${dateOf( after )} - ${dateOf( before )}`;
			} )();

			return ( dateString || placeholder );
		},

		hasErrors() {
			return typeof this.displayError === 'string' && !!this.displayError;
		},

		parsedTypedValue() {
			const { typedValue } = this;

			// May 1, 2020
			const fullDateRegex = /([a-zA-Z]+) (\d+), (\d{4})/;

			if ( fullDateRegex.test( typedValue ) ) {
				const pieces             = typedValue.match( fullDateRegex );
				const [month, day, year] = pieces.slice( 1 );

				if ( months.indexOf( month ) === -1 ) {
					return '';
				}

				if ( !this.isADate( typedValue ) ) {
					return '';
				}

				return `${month} ${day}, ${year}`;
			}

			// 06/07/2020 or 6/7/2020 or 6/7/20
			const mmddyyRegex = /(\d{1,2})(?:\/|-)?(\d{1,2})(?:\/|-)?(\d{2,4})/;

			if ( mmddyyRegex.test( typedValue ) && typedValue.length > 5 ) {
				const pieces                = typedValue.match( mmddyyRegex );
				const [monthNum, day, year] = pieces.slice( 1 );

				const month = months[parseInt( monthNum, 10 ) - 1];

				return `${month} ${day}, ${year}`;
			}

			return '';
		}

	},

	created() {
		this.localValue  = this.value;
		this.passedValue = this.value;
		this.typedValue  = this.value ? dateOf( this.value ) : this.value;
	},

	watch : {

		value( value ) {
			this.displayError = null;

			if ( value !== this.localValue ) {
				this.localValue = value;
			}
		},

		parsedTypedValue( value ) {

			if ( !this.isADate( value ) ) {
				this.passedValue = '';

				return;
			}

			if ( new Date( value ).toISOString() !== this.passedVal ) {
				this.setDate( value );
			}

		},

		localValue( value ) {
			if ( value !== this.value ) {
				this.$emit( 'input', value );
			}
		},

		errors( errors ) {
			this.displayError = errors;
		}

	},

	methods : {


		stopIfAlphaNumeric( e ) {
			const typed = String.fromCharCode( e.which );
			if ( typed.match( /[a-zA-Z0-9]/ ) ) {
				e.stopPropagation();
			}
		},

		focusInput( e ) {
			if ( !this.$refs.textInput ) {
				return;
			}

			const typed = String.fromCharCode( e.which );
			if ( !typed.match( /[a-zA-Z0-9]/ ) ) {
				return;
			}

			this.$refs.textInput.focus();
		},

		setCloseTimeout() {

			this.closeTimeout = window.setTimeout( () => {
				this.open = false;
			}, 200 );
		},

		clearCloseTimeout() {
			if ( this.closeTimeout ) {
				window.clearTimeout( this.closeTimeout );
			}
		},

		toggleDropdown() {
			this.open = !this.open;
		},

		setLocalValue( d ) {
			if ( this.options.allowRange ) {
				return;
			}

			if ( Object.keys( d ).length === 0 ) {
				return;
			}

			const { date, month, year } = d;
			const selectedDate = new Date( `${month + 1}/${date}/${year} 0:00 GMT-0000` );

			this.localValue  = selectedDate.toISOString();
			this.typedValue  = dateOf( selectedDate );
			this.open        = false;
		},

		highlightAll() {
			window.setTimeout( () => this.$refs.textInput.setSelectionRange( 0, this.typedValue.length ), 50 );
		},

		setLocalValueFromRange( e ) {
			if ( !this.options.allowRange ) {
				return;
			}

			this.localValue = e;
		},

		isADate : date => ( date && !!new Date( date ).getTime() ),

		setDate( val ) {

			const checkIfRangeDate = ( range ) => {

				const splitDate = range.split( '-' );

				return ( this.isADate( splitDate[0] ) ) && ( this.isADate( splitDate[1] ) );

			};

			// if its a valid date
			if ( this.isADate( val ) || checkIfRangeDate( val ) ) {

				this.passedValue = new Date( val ).toISOString();

				return;

			}

			this.passedValue = '';

		},

		checkForYear() {
			const { typedValue } = this;
			// May 1, 2020
			const fullDateRegex = /([a-zA-Z]+) (\d+), (\d{4})/;

			// May 1
			const fullDateMinusYearRegex = /([a-zA-Z]+) (\d+)/;

			if ( fullDateMinusYearRegex.test( typedValue ) && !fullDateRegex.test( typedValue ) ) {
				const pieces       = typedValue.match( fullDateMinusYearRegex );
				const [month, day] = pieces.slice( 1 );
				const date = new Date();
				const year = date.getFullYear();

				if ( months.indexOf( month ) === -1 ) {
					this.typedValue = '';
				}

				this.typedValue = `${month} ${day}, ${year}`;
			}

			// 06/07 or 6/7 or 0607
			const mmddRegex = /(\d{1,2})(?:\/|-)?(\d{1,2})/;

			if ( mmddRegex.test( typedValue ) && !fullDateRegex.test( typedValue ) ) {
				const pieces       = typedValue.match( mmddRegex );
				const [monthNum, day] = pieces.slice( 1 );
				const date = new Date();
				const year = date.getFullYear();

				const month = months[parseInt( monthNum, 10 ) - 1];

				this.typedValue = `${month} ${day}, ${year}`;
			}

		},
	},

	components : {
		DateDropdown,
	}
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

.date-input {
	width: 200px;
	position: relative;
	cursor: pointer;

	&::before {
		top: 50% !important;
		transform: translateY( -50% );
	}

	&:not(.open) {
		border-bottom-left-radius: 15px;
		border-bottom-right-radius: 15px;

		.select-display {

			&::before {
				transform: translate(6px, -50%) rotate(45deg);
			}

			&::after {
				transform: translate(0, -50%) rotate(-45deg);
			}
		}

		.date-dropdown {
			opacity: 0;
			pointer-events: none;
		}
	}

	&.open {
		z-index: 10;
	}

	&.optional:not(.has-value):not(.open) {
		border: 2px dashed $input-color-faded;

		.select-display {
			padding: 13px;
		}
	}

	&.inline {
		box-shadow: none;
		width: auto;

		// .select-display {
		// 	display: flex;
		// }

		// .minimal-label {
		// 	position: relative !important;
		// 	transform: none !important;
		// 	left: unset !important;
		// 	top: unset !important;
		// 	font-size: 16px !important;
		// 	margin-right: 5px;
		// }

		// .main-display {
		// 	transform: none !important;
		// }
	}

	.select-display {
		padding: 15px;
		width: 100%;
		position: relative;
    z-index: 2;
    background: white;
		border-radius: 15px;

		&.has-min-label {

			p.minimal-label {
				opacity: 1;
				transform: translate3d(0,-7px,0);
			}

			.main-display, .input-container {
				transform: translate3d(0,7px,0);
			}
		}

		&::before, &::after {
			content: ' ';
			height: 10px;
			width: 2px;
			background: $input-color;
			border-radius: 12px;
			right: 30px;
			position: absolute;
			transition: .2s ease-in-out;
			top: 50%;
			transform: translate3d( 0, -50%, 0 );
			z-index: 2;
		}

		&::before {
			transform: translate3d(3px, -50%, 0) rotate(45deg);
		}

		&::after {
			transform: translate3d(3px, -50%, 0) rotate(-45deg);
		}

		.main-display {
			transition: transform 0.2s ease;
		}

		.input-container {

			.input-date {
				width: 100%;
			}
		}

		p {
			color: $input-color;
			font-weight: 500;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
			margin: 0;
			font-size: 16px;

			&.minimal-label {
				color: $color-primary;
				font-size: 12px;
				opacity: 0;
				transform: translate3d(0,0,0);
				position: absolute;
				left: 15px;
				top: 15px;
				transition: opacity 0.2s ease, transform 0.2s ease;
				pointer-events: none;
			}
		}
	}

	.date-dropdown {
		position: absolute;
		top: calc(50% + 18px);
		right: 0;
		z-index: 2;
		opacity: 1;
		pointer-events: all;
		transition: opacity 0.2s ease;

		> .arrow {
			left: calc(100% - 28px);
			pointer-events: none;
		}
	}
}
</style>
